import { Component, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { AnalyticsService } from '@shared/services/analytics.service';
import { ActivatedRoute } from '@angular/router';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { LoadState } from '@app/main/analytics/loader-directive/loader.module';
import { saveAs } from 'file-saver';
import { Course } from '@shared/models';
import { toSnakeCase } from '@shared/utils/string.utils';

const RECEIVED_AI_MESSAGE = 'received message from AI agent';
const SENT_AI_MESSAGE = 'sent message to AI agent';

@Component({
  selector: 'xapi-log',
  templateUrl: './xapi-log.component.html',
  styleUrls: ['./xapi-log.component.scss']
})
export class XAPILogComponent implements OnInit {
  @Input() sessionId: string;
  @Input() programsMode = false;
  @Input() course: Course;

  public noData = false;
  public loadStatus: string;
  public dataSource: MatTableDataSource<any>;
  @Output() onGoBack = new EventEmitter<void>();
  @ViewChild(MatTable, { static: false }) table: MatTable<any>;
  displayedColumns: string[] = ['date', 'object', 'type', 'verb', 'result'];
  userName: string;
  sessionDate: Date;
  constructor(
    private _analyticsService: AnalyticsService
  ) {

  }

  ngOnInit(): void {
    this.displayedColumns = this.programsMode ? ['date', 'object', 'result'] : ['date', 'object', 'type', 'verb', 'result'];
    
    this.loadStatus = LoadState.loading;

    this._analyticsService.getSessionLog(this.sessionId).subscribe(logs => {
      this.loadStatus = LoadState.completed;
      if (!logs || logs.length === 0) {
        this.noData = true;
        return;
      }
      this.userName = logs[0].actor_name;
      this.sessionDate = logs[0].date;
      // Get basic course sessions
      this.dataSource = new MatTableDataSource(this.setupDataSource(logs));
    }, () => {
      this.loadStatus = LoadState.error;
    });

  }
  formatResult(result: any): string {
    if (!result) {
      return '';
    }
    if (result.response) {
      return 'with response ' + result.response;
    }
    if (result.score && result.score.raw) {
      return 'with score ' + result.score.raw;
    }
    if (result.extensions && result.extensions['https://spaces&46;wondavr&46;com/xapi/extension/content']) {
      return result.extensions['https://spaces&46;wondavr&46;com/xapi/extension/content']
    }
    return '';
  }
  formatActor(name: string): string {
    if (name.indexOf('Anonymous') > -1) {
      return 'Anonymous';
    }
    return name;
  }
  formatVerb(verb: string): string {
    switch (verb) {
      case RECEIVED_AI_MESSAGE:
        return 'received AI message';
      case SENT_AI_MESSAGE:
        return 'sent AI message'
      default:
        return verb
    }
  }
  getType(verb: string): string {
    switch (verb) {
      case 'launched':
      case 'completed':
      case 'passed':
        return 'Experience';
      case 'experienced':
        return 'Scene';
      case 'answered':
        return 'Quiz';
      case 'interacted':
      case RECEIVED_AI_MESSAGE:
      case SENT_AI_MESSAGE:
        return 'Annotation';
      default:
        return '';

    }
  }
  sortData(event: any): void {
    this.dataSource.data = this.dataSource.data.sort((a: any, b: any) => {
      if (a[event.active] < b[event.active]) {
        return event.direction === 'asc' ? 1 : -1;
      } else if (a[event.active] > b[event.active]) {
        return event.direction === 'asc' ? -1 : 1;
      } else {
        return 0;
      }
    });
  }
  setupDataSource(logs: any[]): any[] {
    if (!this.programsMode) return logs;
    return logs
      .filter(log => [RECEIVED_AI_MESSAGE, SENT_AI_MESSAGE].includes(log.verb))
      .map(log => {
        return {
          ...log,
          sender: log.verb === SENT_AI_MESSAGE ? this.userName : 'AI character'
        };
      });
  }
  goBack(): void {
    this.onGoBack.emit();
  }
  exportStats() {
    this._analyticsService.getSessionLog(this.sessionId, 'csv')
    .subscribe(data => {
      const fileName = `Wonda_export_${toSnakeCase(this.course.title)}_session_${this.sessionId}_${new Date().toISOString().split('T')[0]}`;
      const blob = new Blob([data], {type: 'text/csv' })
      saveAs(blob, fileName);
    })
  }
}
